import React from 'react';
import styled, { css } from 'styled-components';
import { ClipLoader } from 'react-spinners';
import { Flex } from '@dutchie/common/components/rebass-compat';

export function FullPageLoader({ color = '#61a58b' }) {
  return (
    <Flex justifyContent='center' alignItems='center' style={{ height: '50vh', minHeight: '700px' }}>
      <ClipLoader color={color} size={100} />
    </Flex>
  );
}

/**
 * @type {JSX.Element}
 *
 */
export function SmallLoader({ spinnerSize = null, height = 60, width = null, color = '#61a58b', ...props }) {
  return (
    <Container height={height} width={width} {...props}>
      <ClipLoader color={color} size={spinnerSize || height} />
    </Container>
  );
}

/**
 * @param {Object} props - Component props
 * @param {number} [props.height=60] - Height of the skeleton in pixels
 * @param {number|null} [props.width=null] - Width of the skeleton in pixels. If null, defaults to 100%
 * @param {boolean} [props.isLeftAligned=false] - Whether to align the skeleton to the left
 * @param {boolean} [props.useExactDimensions=false] - Whether to use the exact dimensions of the skeleton
 * @param {Object} [props...rest] - Additional props are spread to the container element
 * @returns {JSX.Element} A skeleton loader component with animation
 */
export function SkeletonLoader({
  height = 60,
  width = null,
  isLeftAligned = false,
  useExactDimensions = false,
  ...props
}) {
  return (
    <Container
      className='skeleton-loading'
      height={height}
      width={width}
      $isLeftAligned={isLeftAligned}
      $useExactDimensions={useExactDimensions}
      {...props}
    >
      <Skeleton $isLeftAligned={isLeftAligned} $useExactDimensions={useExactDimensions} />
    </Container>
  );
}

const Container = styled(Flex)`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: ${({ height, $useExactDimensions }) => ($useExactDimensions ? `${height}px` : `${height - 10}px`)};
  display: flex;
  justify-content: ${({ $isLeftAligned }) => ($isLeftAligned ? 'flex-start' : 'center')};
  align-items: center;

  .skeleton-loading {
    padding: 0;
    justify-content: flex-end;
    align-items: flex-start;
  }
`;

const Skeleton = styled.div`
  height: ${({ $useExactDimensions }) => ($useExactDimensions ? '100%' : '75%')};
  width: ${({ $useExactDimensions }) => ($useExactDimensions ? '100%' : '50%')};
  animation: skeleton-loading 1s linear infinite alternate;
  background-color: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 4px;

  ${({ $isLeftAligned }) =>
    !$isLeftAligned &&
    css`
      margin-left: auto;
    `};

  @keyframes skeleton-loading {
    0% {
      background-color: hsl(200, 0%, 90%);
    }
    100% {
      background-color: hsl(200, 0%, 70%);
    }
  }
`;
